import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';

/**
 * Sentry init (Day 11) + Day 14 polish: PII scrubbing.
 *
 * Init runs once at app boot. DSN comes from VITE_SENTRY_DSN — when unset,
 * Sentry simply does nothing, so local dev / preview builds without a DSN
 * keep working. Environment is taken from VITE_APP_ENV (set per Vercel
 * environment).
 *
 * The ErrorBoundary in src/components/ErrorBoundary.tsx already catches
 * render errors; we wire its componentDidCatch into captureException via
 * the exported `reportError` helper.
 *
 * Cross-border data flow: with VITE_SENTRY_DSN pointing at the EU region
 * (ingest.de.sentry.io), error metadata leaves Canada bound for Frankfurt.
 * The `beforeSend` scrubber below tightens what actually leaves the page —
 * UUIDs in URLs, email addresses, JWTs, and Bearer tokens are replaced with
 * inert placeholders. This complements the privacy-default Sentry config
 * (sendDefaultPii: false, replay maskAllText, etc.) and is what the DPA
 * subprocessor disclosure relies on.
 */

// Mechanical patterns. Names aren't included — they have no clean regex
// shape and mostly leak via error.message strings, which the scrubber walks.
const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const JWT_RE =
  /eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9_.-]+/gi;

function scrubString(s: string): string {
  return s
    .replace(JWT_RE, '<jwt>')
    .replace(BEARER_RE, 'Bearer <token>')
    .replace(EMAIL_RE, '<email>')
    .replace(UUID_RE, '<uuid>');
}

function scrubValue(v: unknown): unknown {
  if (typeof v === 'string') return scrubString(v);
  if (Array.isArray(v)) return v.map(scrubValue);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, vv] of Object.entries(v as Record<string, unknown>)) {
      out[k] = scrubValue(vv);
    }
    return out;
  }
  return v;
}

function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (typeof event.message === 'string') event.message = scrubString(event.message);
  if (event.request) event.request = scrubValue(event.request) as typeof event.request;
  if (event.tags) event.tags = scrubValue(event.tags) as typeof event.tags;
  if (event.extra) event.extra = scrubValue(event.extra) as typeof event.extra;
  if (event.contexts) event.contexts = scrubValue(event.contexts) as typeof event.contexts;
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      message: typeof b.message === 'string' ? scrubString(b.message) : b.message,
      data: b.data ? (scrubValue(b.data) as typeof b.data) : b.data,
    }));
  }
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((v) => ({
      ...v,
      value: typeof v.value === 'string' ? scrubString(v.value) : v.value,
    }));
  }
  return event;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_APP_ENV as string | undefined) ?? 'development',
    release: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event: ErrorEvent) {
      try {
        return scrubEvent(event);
      } catch {
        // Fail-closed for privacy: drop the event entirely if scrubbing
        // throws, rather than risk shipping un-scrubbed PII.
        return null;
      }
    },
  });
}

/**
 * Capture an unhandled render error from ErrorBoundary.
 * Safe to call when Sentry isn't initialised — becomes a no-op.
 */
export function reportError(error: unknown, extra?: Record<string, unknown>): void {
  Sentry.captureException(error, { extra });
}
